import moment from "moment";
import { Metric } from "../../../../cores/analytics/interface/api/get-insights";
import { FacebookPagePost } from "../../../../cores/analytics/interface/api/get-facebook-posts";
import { InstagramPost } from "../../../../cores/analytics/interface/api/get-instagram-feed-posts";
import { analyticsApiService } from "../../../../cores/analytics/services/api";
import { tiktokApiService } from "../../../../cores/tiktok/services/api";
import { TikTokAnalyticsSnapshot } from "../tiktokAnalyticsMock";
import { SyncedPlatforms, SummaryPlatform } from "./summaryAnalyticsTypes";

export interface RawSummaryData {
  since: string;
  until: string;
  previousSince: string;
  previousUntil: string;
  facebook?: {
    insights: Metric[];
    previousInsights: Metric[];
    posts: FacebookPagePost[];
    previousPosts: FacebookPagePost[];
  };
  instagram?: {
    insights: Metric[];
    previousInsights: Metric[];
    posts: InstagramPost[];
    previousPosts: InstagramPost[];
  };
  tiktok?: {
    snapshot: TikTokAnalyticsSnapshot;
    previousSnapshot: TikTokAnalyticsSnapshot;
  };
}

const toIso = (raw: string) => {
  const m = moment(raw, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : raw;
};

const previousRange = (since: string, until: string) => {
  const start = moment(since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  const end = moment(until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
  if (!start.isValid() || !end.isValid()) {
    return { previousSince: since, previousUntil: until };
  }
  const days = end.diff(start, "days") + 1;
  const prevEnd = start.clone().subtract(1, "day");
  const prevStart = prevEnd.clone().subtract(days - 1, "days");
  return {
    previousSince: prevStart.format("YYYY-MM-DD"),
    previousUntil: prevEnd.format("YYYY-MM-DD"),
  };
};

export interface FetchSummaryAnalyticsOptions {
  onPlatformLoaded?: (raw: RawSummaryData, platform: SummaryPlatform) => void;
  onPlatformSettled?: (platform: SummaryPlatform) => void;
}

const cloneRaw = (src: RawSummaryData): RawSummaryData => ({
  since: src.since,
  until: src.until,
  previousSince: src.previousSince,
  previousUntil: src.previousUntil,
  ...(src.facebook ? { facebook: src.facebook } : {}),
  ...(src.instagram ? { instagram: src.instagram } : {}),
  ...(src.tiktok ? { tiktok: src.tiktok } : {}),
});

export async function fetchSummaryAnalytics(
  companyId: string,
  since: string,
  until: string,
  platforms: SyncedPlatforms,
  options?: FetchSummaryAnalyticsOptions
): Promise<RawSummaryData> {
  const sinceIso = toIso(since);
  const untilIso = toIso(until);
  const { previousSince, previousUntil } = previousRange(sinceIso, untilIso);

  const tasks: Promise<void>[] = [];
  const result: RawSummaryData = {
    since: sinceIso,
    until: untilIso,
    previousSince,
    previousUntil,
  };

  if (platforms.facebook) {
    tasks.push(
      (async () => {
        try {
          const [daily, lifetime, prevDaily, prevLifetime, posts, prevPosts] =
            await Promise.all([
              analyticsApiService.facebookInsights(
                companyId,
                sinceIso,
                untilIso,
                "Daily"
              ),
              analyticsApiService.facebookInsights(
                companyId,
                sinceIso,
                untilIso,
                "Lifetime",
                10
              ),
              analyticsApiService.facebookInsights(
                companyId,
                previousSince,
                previousUntil,
                "Daily"
              ),
              analyticsApiService.facebookInsights(
                companyId,
                previousSince,
                previousUntil,
                "Lifetime",
                10
              ),
              analyticsApiService
                .facebookPublications(companyId, sinceIso, untilIso)
                .then((p) => p?.post || []),
              analyticsApiService
                .facebookPublications(companyId, previousSince, previousUntil)
                .then((p) => p?.post || []),
            ]);
          result.facebook = {
            insights: [...daily, ...lifetime],
            previousInsights: [...prevDaily, ...prevLifetime],
            posts,
            previousPosts: prevPosts,
          };
          options?.onPlatformLoaded?.(cloneRaw(result), "facebook");
        } catch {
          // Skip failed platform; others can still render.
        } finally {
          options?.onPlatformSettled?.("facebook");
        }
      })()
    );
  }

  if (platforms.instagram) {
    tasks.push(
      (async () => {
        try {
          const [daily, lifetime, prevDaily, prevLifetime, posts, prevPosts] =
            await Promise.all([
              analyticsApiService.instagramInsights(
                companyId,
                sinceIso,
                untilIso,
                "Daily"
              ),
              analyticsApiService.instagramInsights(
                companyId,
                sinceIso,
                untilIso,
                "Lifetime",
                10
              ),
              analyticsApiService.instagramInsights(
                companyId,
                previousSince,
                previousUntil,
                "Daily"
              ),
              analyticsApiService.instagramInsights(
                companyId,
                previousSince,
                previousUntil,
                "Lifetime",
                10
              ),
              analyticsApiService
                .instagramPublications(companyId, sinceIso, untilIso, "all")
                .then((p) => p?.post || []),
              analyticsApiService
                .instagramPublications(
                  companyId,
                  previousSince,
                  previousUntil,
                  "all"
                )
                .then((p) => p?.post || []),
            ]);
          result.instagram = {
            insights: [...daily, ...lifetime],
            previousInsights: [...prevDaily, ...prevLifetime],
            posts,
            previousPosts: prevPosts,
          };
          options?.onPlatformLoaded?.(cloneRaw(result), "instagram");
        } catch {
          // Skip failed platform; others can still render.
        } finally {
          options?.onPlatformSettled?.("instagram");
        }
      })()
    );
  }

  if (platforms.tiktok) {
    tasks.push(
      (async () => {
        try {
          const [snapshot, previousSnapshot] = await Promise.all([
            tiktokApiService.getInsights(
              companyId,
              sinceIso,
              untilIso,
              "Lifetime",
              10
            ),
            tiktokApiService.getInsights(
              companyId,
              previousSince,
              previousUntil,
              "Lifetime",
              10
            ),
          ]);
          result.tiktok = { snapshot, previousSnapshot };
          options?.onPlatformLoaded?.(cloneRaw(result), "tiktok");
        } catch {
          // Skip failed platform; others can still render.
        } finally {
          options?.onPlatformSettled?.("tiktok");
        }
      })()
    );
  }

  await Promise.allSettled(tasks);
  return cloneRaw(result);
}
